<template>
  <div class="company-management">
    <v-container grid-list-md>
      <h1 class="display-1 font-weight-bold mb-2">Empresas</h1>
      <p class="subheading grey--text mb-4">
        Administra desde un solo lugar las empresas precargadas y las que ya concluyeron su registro.
      </p>

      <v-alert v-if="generalError" type="error" dismissible :value="true" @input="generalError = ''">
        {{ generalError }}
      </v-alert>

      <v-card class="elevation-2">
        <v-tabs v-model="activeTab" color="primary" dark slider-color="white">
          <v-tab><v-icon left>playlist_add_check</v-icon>Precargadas y onboarding</v-tab>
          <v-tab><v-icon left>business</v-icon>Empresas activas</v-tab>

          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-layout row wrap align-center class="mb-3">
                  <v-flex xs12 md6>
                    <v-btn color="primary" large @click="openManualDialog">
                      <v-icon left>add</v-icon>Agregar empresa
                    </v-btn>
                  </v-flex>
                  <v-flex xs12 md6 class="text-md-right">
                    <v-btn flat icon :loading="loading" @click="fetchCompanies"><v-icon>refresh</v-icon></v-btn>
                  </v-flex>
                </v-layout>

                <v-layout row wrap>
                  <v-flex xs12 md7>
                    <v-text-field v-model="search" prepend-icon="search" label="Buscar empresa, correo u oficina" clearable @keyup.enter="fetchCompanies"></v-text-field>
                  </v-flex>
                  <v-flex xs12 md5>
                    <v-select v-model="statusFilter" :items="statusFilterOptions" item-text="text" item-value="value" label="Filtrar por estatus" clearable @change="fetchCompanies"></v-select>
                  </v-flex>
                </v-layout>

                <v-layout row wrap class="mb-3">
                  <v-flex xs12>
                    <v-btn color="primary" :disabled="!hasSelectionChanges" :loading="savingSelection" @click="saveSelection">
                      <v-icon left>save</v-icon>Guardar selección
                    </v-btn>
                    <v-btn color="success" :disabled="selectedReadyIds.length === 0" :loading="sending" @click="confirmSend">
                      <v-icon left>send</v-icon>Enviar onboarding ({{ selectedReadyIds.length }})
                    </v-btn>
                  </v-flex>
                </v-layout>

                <v-data-table :headers="preloadHeaders" :items="companies" :loading="loading" :rows-per-page-items="[10, 25, 50]" :rows-per-page="25" no-data-text="No hay empresas precargadas" class="elevation-1">
                  <template v-slot:items="props">
                    <td class="text-xs-center">
                      <v-checkbox :input-value="selectedIds.indexOf(props.item.id) !== -1" :disabled="!isSelectable(props.item)" hide-details color="primary" @change="toggleSelection(props.item, $event)"></v-checkbox>
                    </td>
                    <td>{{ props.item.name }}</td>
                    <td>{{ officeNames(props.item) }}</td>
                    <td>{{ props.item.onboarding_email || 'Sin correo' }}</td>
                    <td class="text-xs-center">
                      <v-chip small :color="statusColor(props.item.status)" text-color="white">{{ statusLabel(props.item.status) }}</v-chip>
                    </td>
                    <td class="text-xs-center nowrap-actions">
                      <v-btn flat icon small title="Editar empresa" @click="openEditDialog(props.item)">
                        <v-icon small>edit</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="isBlocked(props.item)"
                        flat
                        icon
                        small
                        color="success"
                        title="Desbloquear empresa"
                        :loading="unblockingId === props.item.id"
                        @click="openUnblockDialog(props.item)"
                      >
                        <v-icon small>lock_open</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="canResendInvitation(props.item)"
                        flat
                        icon
                        small
                        color="primary"
                        title="Reenviar invitación"
                        :loading="resendingId === props.item.id"
                        @click="openResendDialog(props.item)"
                      >
                        <v-icon small>refresh</v-icon>
                      </v-btn>
                    </td>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card flat>
              <v-card-text>
                <v-layout row wrap align-center>
                  <v-flex xs12 md8><v-text-field v-model="activeSearch" prepend-icon="search" label="Buscar empresa activa" clearable></v-text-field></v-flex>
                  <v-flex xs12 md4 class="text-md-right"><v-btn flat icon :loading="loadingActive" @click="fetchActiveCompanies"><v-icon>refresh</v-icon></v-btn></v-flex>
                </v-layout>
                <v-data-table :headers="activeHeaders" :items="activeCompanies" :search="activeSearch" :loading="loadingActive" :rows-per-page-items="[10, 25, 50]" :rows-per-page="25" no-data-text="No hay empresas activas en el edificio" class="elevation-1">
                  <template v-slot:items="props">
                    <td>{{ props.item.name }}</td>
                    <td>{{ props.item.address || '-' }}</td>
                    <td>{{ props.item.telephone || '-' }}</td>
                    <td>{{ props.item.email || '-' }}</td>
                    <td>{{ props.item.web_page || '-' }}</td>
                    <td class="text-xs-center">
                      <v-chip small :color="props.item.enabled ? 'success' : 'error'" text-color="white">
                        {{ props.item.enabled ? 'Activa' : 'Bloqueada' }}
                      </v-chip>
                    </td>
                    <td class="text-xs-center">
                      <v-btn
                        small
                        flat
                        :color="props.item.enabled ? 'error' : 'success'"
                        :disabled="blockingId !== null"
                        @click="openBlockDialog(props.item)"
                      >
                        <v-icon left small>{{ props.item.enabled ? 'block' : 'lock_open' }}</v-icon>
                        {{ props.item.enabled ? 'Bloquear' : 'Desbloquear' }}
                      </v-btn>
                    </td>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </v-card>
    </v-container>

    <v-dialog v-model="manualDialog" max-width="600px" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Agregar empresa</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="manualDialog = false"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text>
          <v-form ref="manualForm" v-model="manualFormValid">
            <v-alert v-if="!loadingOffices && !availableOffices.length" type="warning" :value="true" class="mb-3">
              No hay oficinas disponibles. Registra una en el catálogo de oficinas antes de continuar.
            </v-alert>
            <v-select v-model="manualForm.office_id" :items="availableOffices" item-text="code" item-value="id" label="Oficina *" prepend-icon="meeting_room" :loading="loadingOffices" :rules="requiredRules" :error-messages="manualErrors.office_id" no-data-text="No hay oficinas disponibles"></v-select>
            <v-text-field v-model="manualForm.company_name" label="Cliente o empresa *" prepend-icon="business" :rules="requiredRules" :error-messages="manualErrors.company_name"></v-text-field>
            <v-text-field v-model="manualForm.email" label="Correo de onboarding" prepend-icon="email" type="email" hint="Si aún no lo tienes, quedará marcada como falta de contacto." persistent-hint :error-messages="manualErrors.email"></v-text-field>
            <v-radio-group v-model="manualForm.access_status" row>
              <v-radio label="Lista para acceso" value="READY" color="success"></v-radio>
              <v-radio label="Bloqueada / no autorizar" value="BLOCKED" color="error"></v-radio>
            </v-radio-group>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="manualDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="savingManual" @click="saveManualEntry">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="editDialog" max-width="520px">
      <v-card>
        <v-card-title class="headline">Editar empresa precargada</v-card-title>
        <v-card-text v-if="editingCompany">
          <v-text-field v-model="editForm.name" label="Nombre de la empresa"></v-text-field>
          <v-text-field v-model="editForm.onboarding_email" label="Correo para onboarding" type="email" :error-messages="editErrors.onboarding_email"></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer><v-btn flat @click="editDialog = false">Cancelar</v-btn><v-btn color="primary" :loading="savingCompany" @click="saveCompany">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="sendDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Enviar invitaciones</v-card-title>
        <v-card-text>Se enviará un enlace de onboarding a {{ selectedReadyIds.length }} empresa(s). Cada enlace tendrá una vigencia de 24 horas.</v-card-text>
        <v-card-actions><v-spacer></v-spacer><v-btn flat @click="sendDialog = false">Cancelar</v-btn><v-btn color="success" :loading="sending" @click="sendInvitations">Enviar</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="resendDialog" max-width="520px" persistent>
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Reenviar invitación</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon :disabled="resendingId !== null" @click="closeResendDialog"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text v-if="companyToResend" class="pt-4">
          <v-alert v-if="resendError" type="error" :value="true" class="mb-3">{{ resendError }}</v-alert>
          <p>
            Se enviará un nuevo enlace de onboarding a
            <strong>{{ companyToResend.onboarding_email }}</strong> para
            <strong>{{ companyToResend.name }}</strong>.
          </p>
          <v-alert
            v-if="companyToResend.status === 'ONBOARDING_IN_PROGRESS'"
            type="warning"
            :value="true"
          >
            Esta empresa ya inició su registro. El enlace anterior dejará de funcionar y deberá continuar con el nuevo correo enviado.
          </v-alert>
          <v-alert v-else type="info" :value="true">
            El enlace anterior será revocado y el nuevo tendrá una vigencia de 24 horas.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat :disabled="resendingId !== null" @click="closeResendDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="resendingId !== null" @click="resendInvitation">
            <v-icon left>refresh</v-icon>Reenviar correo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="unblockDialog" max-width="520px" persistent>
      <v-card>
        <v-toolbar dark color="success">
          <v-toolbar-title>Desbloquear empresa</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon :disabled="unblockingId !== null" @click="closeUnblockDialog"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text v-if="companyToUnblock" class="pt-4">
          <v-alert v-if="unblockError" type="error" :value="true" class="mb-3">{{ unblockError }}</v-alert>
          <p>¿Desbloquear a <strong>{{ companyToUnblock.name }}</strong>?</p>
          <ul class="mb-3">
            <li v-if="companyToUnblock.linked_company">Sus usuarios volverán a estar activos y se le devolverán los cajones que sigan libres.</li>
            <li v-else-if="companyToUnblock.onboarding_email">Quedará como "Lista" y podrás seleccionarla para enviarle el onboarding.</li>
            <li v-else>Quedará como "Falta contacto": captura su correo para poder enviarle el onboarding.</li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat :disabled="unblockingId !== null" @click="closeUnblockDialog">Cancelar</v-btn>
          <v-btn color="success" :loading="unblockingId !== null" @click="confirmUnblock">Desbloquear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="blockDialog" max-width="520px" persistent>
      <v-card>
        <v-toolbar dark :color="companyToBlock && companyToBlock.enabled ? 'error' : 'success'">
          <v-toolbar-title>{{ companyToBlock && companyToBlock.enabled ? 'Bloquear empresa' : 'Desbloquear empresa' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon :disabled="blockingId !== null" @click="closeBlockDialog"><v-icon>close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text v-if="companyToBlock" class="pt-4">
          <v-alert v-if="blockError" type="error" :value="true" class="mb-3">{{ blockError }}</v-alert>
          <template v-if="companyToBlock.enabled">
            <p>¿Bloquear a <strong>{{ companyToBlock.name }}</strong>?</p>
            <ul class="mb-3">
              <li>Todos sus usuarios quedarán inactivos y no podrán iniciar sesión.</li>
              <li>Sus cajones se desvincularán y se liberará a los empleados que los tenían.</li>
            </ul>
          </template>
          <template v-else>
            <p>¿Desbloquear a <strong>{{ companyToBlock.name }}</strong>?</p>
            <ul class="mb-3">
              <li>Todos sus usuarios volverán a estar activos.</li>
              <li>Se le devolverán los cajones que sigan libres. Los empleados deberán asignarse de nuevo.</li>
            </ul>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat :disabled="blockingId !== null" @click="closeBlockDialog">Cancelar</v-btn>
          <v-btn
            :color="companyToBlock && companyToBlock.enabled ? 'error' : 'success'"
            :loading="blockingId !== null"
            @click="confirmBlock"
          >
            {{ companyToBlock && companyToBlock.enabled ? 'Bloquear' : 'Desbloquear' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import store from '@/store'

const API_BASE_URL = process.env.VUE_APP_API_URL || 'https://empresa-service.intrare.app'

export default {
  name: 'CorporateBuildingCompanies',
  data() {
    return {
      activeTab: 0,
      companies: [],
      activeCompanies: [],
      availableOffices: [],
      loading: false,
      loadingActive: false,
      loadingOffices: false,
      savingSelection: false,
      savingCompany: false,
      savingManual: false,
      sending: false,
      search: '',
      activeSearch: '',
      statusFilter: null,
      selectedIds: [],
      originalSelectedIds: [],
      generalError: '',
      manualDialog: false,
      manualFormValid: false,
      manualErrors: {},
      manualForm: { office_id: null, company_name: '', email: '', access_status: 'READY' },
      editDialog: false,
      sendDialog: false,
      resendDialog: false,
      companyToResend: null,
      resendingId: null,
      blockDialog: false,
      companyToBlock: null,
      blockingId: null,
      blockError: '',
      unblockDialog: false,
      companyToUnblock: null,
      unblockingId: null,
      unblockError: '',
      resendError: '',
      editingCompany: null,
      editForm: { name: '', onboarding_email: '' },
      editErrors: {},
      requiredRules: [value => !!value || 'Este campo es requerido.'],
      preloadHeaders: [
        { text: 'Seleccionar', value: 'selected', sortable: false, align: 'center' },
        { text: 'Empresa', value: 'name' },
        { text: 'Oficinas', value: 'offices', sortable: false },
        { text: 'Correo de onboarding', value: 'onboarding_email' },
        { text: 'Estatus', value: 'status', align: 'center' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'center' }
      ],
      activeHeaders: [
        { text: 'Empresa', value: 'name' }, { text: 'Dirección', value: 'address' },
        { text: 'Teléfono', value: 'telephone' }, { text: 'Correo', value: 'email' },
        { text: 'Página web', value: 'web_page' }, { text: 'Estatus', value: 'enabled', align: 'center' },
        { text: 'Acciones', value: 'actions', align: 'center', sortable: false }
      ],
      statusFilterOptions: [
        { text: 'Bloqueada', value: 'BLOCKED' }, { text: 'Falta contacto', value: 'MISSING_CONTACT' },
        { text: 'Lista', value: 'READY' }, { text: 'Seleccionada', value: 'SELECTED' },
        { text: 'Invitación enviada', value: 'INVITATION_SENT' },
        { text: 'Onboarding en proceso', value: 'ONBOARDING_IN_PROGRESS' },
        { text: 'Activa', value: 'ACTIVE' }, { text: 'Cancelada', value: 'CANCELLED' }
      ]
    }
  },
  computed: {
    selectedReadyIds() {
      return this.companies.filter(item => item.status === 'SELECTED' && this.selectedIds.indexOf(item.id) !== -1).map(item => item.id)
    },
    hasSelectionChanges() {
      return this.selectedIds.slice().sort((a, b) => a - b).join(',') !== this.originalSelectedIds.slice().sort((a, b) => a - b).join(',')
    }
  },
  watch: {
    activeTab(value) {
      if (value === 1 && !this.activeCompanies.length) this.fetchActiveCompanies()
    }
  },
  mounted() {
    this.fetchCompanies()
    this.fetchAvailableOffices()
  },
  methods: {
    authHeaders() { return { 'Content-Type': 'application/json', Authorization: 'Token ' + store.getters.getToken } },
    notify(message) {
      this.$store.commit('snackbar_change_message', message)
      this.$store.commit('snackbar_activate_true')
    },
    errorMessage(error, fallback) {
      const data = error.response && error.response.data
      return data && data.error ? data.error : fallback
    },
    fetchCompanies() {
      this.loading = true
      const params = {}
      if (this.search) params.search = this.search
      if (this.statusFilter) params.status = this.statusFilter
      axios.get(`${API_BASE_URL}/Parking/building-company-preloads/`, { headers: this.authHeaders(), params: params })
        .then(response => {
          this.companies = Array.isArray(response.data) ? response.data : (response.data.results || [])
          this.selectedIds = this.companies.filter(item => item.status === 'SELECTED').map(item => item.id)
          this.originalSelectedIds = this.selectedIds.slice()
        })
        .catch(error => { this.generalError = this.errorMessage(error, 'No fue posible cargar las empresas.') })
        .finally(() => { this.loading = false })
    },
    fetchActiveCompanies() {
      this.loadingActive = true
      axios.get(`${API_BASE_URL}/parques/empresas`, { headers: this.authHeaders() })
        .then(response => { this.activeCompanies = response.data || [] })
        .catch(error => { this.generalError = this.errorMessage(error, 'No fue posible cargar las empresas activas.') })
        .finally(() => { this.loadingActive = false })
    },
    fetchAvailableOffices() {
      this.loadingOffices = true
      axios.get(`${API_BASE_URL}/Parking/building-offices/`, {
        headers: this.authHeaders(),
        params: { status: 'AVAILABLE' }
      })
        .then(response => {
          this.availableOffices = Array.isArray(response.data) ? response.data : (response.data.results || [])
        })
        .catch(error => { this.generalError = this.errorMessage(error, 'No fue posible cargar las oficinas disponibles.') })
        .finally(() => { this.loadingOffices = false })
    },
    openManualDialog() {
      this.manualErrors = {}
      this.manualForm = { office_id: null, company_name: '', email: '', access_status: 'READY' }
      this.manualDialog = true
      this.$nextTick(() => { if (this.$refs.manualForm) this.$refs.manualForm.resetValidation() })
    },
    saveManualEntry() {
      if (!this.$refs.manualForm.validate()) return
      const data = {
        office_id: this.manualForm.office_id,
        company_name: this.manualForm.company_name,
        email: this.manualForm.email,
        access_status: this.manualForm.access_status
      }
      this.savingManual = true
      this.manualErrors = {}
      axios.post(`${API_BASE_URL}/Parking/building-company-preloads/manual/`, data, { headers: this.authHeaders() })
        .then(response => {
          this.manualDialog = false
          this.notify(response.data.message || 'La información fue guardada.')
          this.fetchCompanies()
          this.fetchAvailableOffices()
        })
        .catch(error => {
          const data = error.response && error.response.data
          if (data && typeof data === 'object') this.manualErrors = data
          else this.generalError = 'No fue posible guardar la información.'
        })
        .finally(() => { this.savingManual = false })
    },
    isSelectable(item) { return item.status === 'READY' || item.status === 'SELECTED' },
    toggleSelection(item, selected) {
      const index = this.selectedIds.indexOf(item.id)
      if (selected && index === -1) this.selectedIds.push(item.id)
      if (!selected && index !== -1) this.selectedIds.splice(index, 1)
    },
    selectionRequest(ids, selected) {
      return axios.post(`${API_BASE_URL}/Parking/building-company-preloads/select/`, { ids: ids, selected: selected }, { headers: this.authHeaders() })
    },
    saveSelection() {
      const toSelect = this.selectedIds.filter(id => this.originalSelectedIds.indexOf(id) === -1)
      const toDeselect = this.originalSelectedIds.filter(id => this.selectedIds.indexOf(id) === -1)
      const requests = []
      if (toSelect.length) requests.push(this.selectionRequest(toSelect, true))
      if (toDeselect.length) requests.push(this.selectionRequest(toDeselect, false))
      if (!requests.length) return
      this.savingSelection = true
      Promise.all(requests)
        .then(responses => {
          const errors = []
          responses.forEach(response => { if (response.data.errors) errors.push.apply(errors, response.data.errors) })
          if (errors.length) this.generalError = errors.map(item => item.detail).join(' ')
          else this.notify('Selección actualizada.')
          if (toSelect.length && this.statusFilter === 'READY') this.statusFilter = 'SELECTED'
          this.fetchCompanies()
        })
        .catch(error => { this.generalError = this.errorMessage(error, 'No fue posible actualizar la selección.') })
        .finally(() => { this.savingSelection = false })
    },
    openEditDialog(item) {
      this.editingCompany = item
      this.editForm = { name: item.name, onboarding_email: item.onboarding_email || '' }
      this.editErrors = {}
      this.editDialog = true
    },
    saveCompany() {
      if (!this.editForm.name || !this.editForm.onboarding_email) {
        this.editErrors = { onboarding_email: ['Nombre y correo son requeridos.'] }
        return
      }
      this.savingCompany = true
      axios.patch(`${API_BASE_URL}/Parking/building-company-preloads/${this.editingCompany.id}/`, this.editForm, { headers: this.authHeaders() })
        .then(() => { this.editDialog = false; this.notify('Empresa actualizada.'); this.fetchCompanies() })
        .catch(error => {
          const data = error.response && error.response.data
          if (data && typeof data === 'object') this.editErrors = data
          else this.generalError = 'No fue posible actualizar la empresa.'
        })
        .finally(() => { this.savingCompany = false })
    },
    confirmSend() {
      if (this.hasSelectionChanges) { this.generalError = 'Guarda primero los cambios de selección.'; return }
      this.sendDialog = true
    },
    sendInvitations() {
      this.sending = true
      axios.post(`${API_BASE_URL}/Parking/building-company-preloads/send-onboarding/`, { ids: this.selectedReadyIds }, { headers: this.authHeaders() })
        .then(response => {
          const errors = response.data.errors || []
          this.sendDialog = false
          if (response.data.sent_count) this.notify(`${response.data.sent_count} invitación(es) enviada(s).`)
          if (errors.length) this.generalError = errors.map(item => item.detail).join(' ')
          this.fetchCompanies()
        })
        .catch(error => { this.generalError = this.errorMessage(error, 'No fue posible enviar las invitaciones.') })
        .finally(() => { this.sending = false })
    },
    canResendInvitation(item) {
      return ['INVITATION_SENT', 'ONBOARDING_IN_PROGRESS'].indexOf(item.status) !== -1 && !!item.onboarding_email
    },
    openResendDialog(item) {
      this.companyToResend = item
      this.resendError = ''
      this.resendDialog = true
    },
    isBlocked(item) { return item.status === 'BLOCKED' || item.source_blocked },
    openUnblockDialog(item) {
      this.companyToUnblock = item
      this.unblockError = ''
      this.unblockDialog = true
    },
    closeUnblockDialog() {
      if (this.unblockingId !== null) return
      this.unblockDialog = false
      this.companyToUnblock = null
      this.unblockError = ''
    },
    confirmUnblock() {
      const company = this.companyToUnblock
      this.unblockingId = company.id
      this.unblockError = ''
      axios.post(`${API_BASE_URL}/Parking/building-company-preloads/${company.id}/unblock/`, {}, { headers: this.authHeaders() })
        .then(() => {
          this.notify(`${company.name} fue desbloqueada.`)
          this.unblockingId = null
          this.closeUnblockDialog()
          this.fetchCompanies()
          if (company.linked_company) this.fetchActiveCompanies()
        })
        .catch(error => {
          this.unblockingId = null
          this.unblockError = this.errorMessage(error, 'No fue posible desbloquear la empresa.')
        })
    },
    openBlockDialog(company) {
      this.companyToBlock = company
      this.blockError = ''
      this.blockDialog = true
    },
    closeBlockDialog() {
      if (this.blockingId !== null) return
      this.blockDialog = false
      this.companyToBlock = null
      this.blockError = ''
    },
    confirmBlock() {
      const company = this.companyToBlock
      const blocked = company.enabled
      this.blockingId = company.id
      this.blockError = ''
      axios.post(
        `${API_BASE_URL}/Parking/building-companies/${company.id}/block/`,
        { blocked: blocked },
        { headers: this.authHeaders() }
      )
        .then(response => {
          const count = blocked ? response.data.released_spaces : response.data.restored_spaces
          this.notify(blocked
            ? `Empresa bloqueada. Cajones desvinculados: ${count}`
            : `Empresa desbloqueada. Cajones restaurados: ${count}`)
          this.blockingId = null
          this.closeBlockDialog()
          this.fetchActiveCompanies()
          this.fetchCompanies()
        })
        .catch(error => {
          this.blockingId = null
          this.blockError = this.errorMessage(error, 'No fue posible actualizar la empresa.')
        })
    },
    closeResendDialog() {
      if (this.resendingId !== null) return
      this.resendDialog = false
      this.companyToResend = null
      this.resendError = ''
    },
    resendInvitation() {
      if (!this.companyToResend) return
      const company = this.companyToResend
      this.resendingId = company.id
      axios.post(
        `${API_BASE_URL}/Parking/building-company-preloads/send-onboarding/`,
        { ids: [company.id] },
        { headers: this.authHeaders() }
      ).then(response => {
        const errors = response.data.errors || []
        if (response.data.sent_count) {
          this.notify(`La invitación de ${company.name} fue reenviada.`)
          this.resendDialog = false
          this.companyToResend = null
        }
        if (errors.length) this.resendError = errors.map(item => item.detail).join(' ')
        this.fetchCompanies()
      }).catch(error => {
        this.resendError = this.errorMessage(error, 'No fue posible reenviar la invitación.')
      }).finally(() => { this.resendingId = null })
    },
    officeNames(item) { return item.offices && item.offices.length ? item.offices.map(office => office.code).join(', ') : '-' },
    statusLabel(status) {
      const labels = { BLOCKED: 'Bloqueada', MISSING_CONTACT: 'Falta contacto', READY: 'Lista', SELECTED: 'Seleccionada', INVITATION_SENT: 'Invitación enviada', ONBOARDING_IN_PROGRESS: 'En proceso', ACTIVE: 'Activa', CANCELLED: 'Cancelada' }
      return labels[status] || status
    },
    statusColor(status) {
      const colors = { BLOCKED: 'error', MISSING_CONTACT: 'orange', READY: 'blue', SELECTED: 'indigo', INVITATION_SENT: 'purple', ONBOARDING_IN_PROGRESS: 'deep-purple', ACTIVE: 'success', CANCELLED: 'grey' }
      return colors[status] || 'grey'
    }
  }
}
</script>

<style scoped>
.company-management { padding: 20px; }
.nowrap-actions { white-space: nowrap; }
</style>
